import { useCallback, useEffect, useRef } from 'react';

import { useAIPageEmojiSuggestion } from '@atlassian/ai-image-generation/use-ai-page-emoji-suggestion';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import { useAIEmojiSuggestionActions } from '../AIEmojiSuggestionStore.ts';
import type { AIEmojiSuggestionSource } from '../../types/AIEmojiSuggestionSource.ts';

type AIEmojiSuggestionForTitleGenArgs = {
	contentId: string;
	title: string | null | undefined;
	isTitleGenerating: boolean | undefined;
	locale: string | undefined;
	pageAlreadyHasEmoji: boolean | undefined;
};

type AIEmojiSuggestionForTitleGenBridgeProps = AIEmojiSuggestionForTitleGenArgs & {
	onSuggestTitleClickedRef: { current: (() => void) | undefined };
};

export const AIEmojiSuggestionForTitleGenBridge = ({
	contentId,
	title,
	isTitleGenerating,
	locale,
	pageAlreadyHasEmoji,
	onSuggestTitleClickedRef,
}: AIEmojiSuggestionForTitleGenBridgeProps): null => {
	const { suggestEmoji: suggestEmojiFromPasteTitle, reset: resetEmojiSuggestionHook } =
		useAIPageEmojiSuggestion({ contentId });

	const { setAISuggestedEmojiId, setAifcSuggestionInFlight, resetForNewSession } =
		useAIEmojiSuggestionActions();

	const isPasteEmojiSuggestionEnabled = isExperimentEnabled(
		'confluence_suggested_emojis_for_title_generation',
	);

	const lastTriggerSourceRef = useRef<AIEmojiSuggestionSource>('pasteAutoTitle');
	const onSuggestTitleClicked = useCallback(() => {
		lastTriggerSourceRef.current = 'suggestTitle';
	}, []);
	onSuggestTitleClickedRef.current = isPasteEmojiSuggestionEnabled
		? onSuggestTitleClicked
		: undefined;

	const wasTitleGeneratingRef = useRef<boolean>(false);
	useEffect(() => {
		if (!isPasteEmojiSuggestionEnabled) {
			wasTitleGeneratingRef.current = isTitleGenerating ?? false;
			return;
		}
		const generating = isTitleGenerating ?? false;
		const justStarted = !wasTitleGeneratingRef.current && generating;
		const justFinished = wasTitleGeneratingRef.current && !generating;
		wasTitleGeneratingRef.current = generating;

		if (justStarted) {
			resetForNewSession(lastTriggerSourceRef.current);
			lastTriggerSourceRef.current = 'pasteAutoTitle';
			resetEmojiSuggestionHook?.();
			return;
		}

		if (!justFinished) {
			return;
		}
		const finalTitle = (title ?? '').trim();
		if (!finalTitle) {
			return;
		}
		if (pageAlreadyHasEmoji) {
			return;
		}
		setAifcSuggestionInFlight(true);
		void suggestEmojiFromPasteTitle({ title: finalTitle, locale })
			.then((emojiId) => {
				if (!emojiId) {
					return;
				}
				setAISuggestedEmojiId(emojiId);
			})
			.catch(() => {
				// Swallow — the upstream useAIPageEmojiSuggestion hook owns its
				// own error telemetry. The .finally below still clears the
				// in-flight flag so the UI does not stay stuck.
			})
			.finally(() => {
				// Always clear in-flight state, even on rejection, so
				// aifcSuggestionInFlight does not stay true for the session.
				setAifcSuggestionInFlight(false);
			});
	}, [
		isTitleGenerating,
		isPasteEmojiSuggestionEnabled,
		title,
		locale,
		suggestEmojiFromPasteTitle,
		resetEmojiSuggestionHook,
		pageAlreadyHasEmoji,
		setAISuggestedEmojiId,
		setAifcSuggestionInFlight,
		resetForNewSession,
	]);

	return null;
};
